import { useCallback, useEffect, useRef, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate, useSearchParams } from 'react-router'
import { authenticationState, customerState } from '@repo/unicity'
import { useCurrentLanguage } from '@/core/utils'
import { cartAtom } from '@/core/cart/cart.store'
import { checkoutUuidAtom } from '@/core/checkout/checkout.store'
import { feelgreatSettingsAtom } from '@/core/feelgreat/feelgreat.store'
import { useLogin } from '@/core/auth/useLogin'
import { userStatusAtom, readStoredUserStatus } from '@/core/auth/user-status.store'
import { buildTaiwanLoginFields } from '../utils/login-fields'
import { getTaiwanPeriod, createTaiwanUuid, getTaiwanFormat, createTaiwanAccount, postTaiwanShipping } from '../services/taiwan.api'
import {
    initialTaiwanAccountForm,
    initialTaiwanShippingForm,
    taiwanFormatDataAtom,
    taiwanCheckoutLoadingAtom,
    taiwanCheckoutErrorsAtom,
    taiwanShipToAtom
} from '../stores/taiwan.store'
import type { TaiwanCreateAccountPayload, TaiwanShippingPayload, TaiwanFormatResponse } from '../types/taiwan.types'

// The Taiwan backend expects the 3-digit district code. Normalize a 5-digit
// postal-code entry to that format (matching legacy Taiwan.js); other values
// are left as entered.
const normalizeZip = (zip: string): string => (zip.length === 5 ? zip.slice(0, 3) : zip)

// The format endpoints can 200 with success:false and either Message_v2 (error
// keys → translated) or errorDetail (localized full-text messages).
const formatErrors = (res: TaiwanFormatResponse, fallback: string, language: string): string[] => {
    if (res.errorDetail && res.errorDetail.length > 0) {
        const en = language.toUpperCase() === 'EN'
        return res.errorDetail.map(e => (en ? e.english : e.native) || e.english || '').filter(Boolean)
    }
    if (res.Message_v2 && res.Message_v2.length > 0) return res.Message_v2
    return [fallback]
}

export const useTaiwanCheckout = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()

    const auth = useAtomValue(authenticationState)
    const customer = useAtomValue(customerState)
    const userStatus = useAtomValue(userStatusAtom)
    const settings = useAtomValue(feelgreatSettingsAtom)
    const cart = useAtomValue(cartAtom)

    const [uuid, setUuid] = useAtom(checkoutUuidAtom)
    const setFormat = useSetAtom(taiwanFormatDataAtom)
    const [shipTo, setShipTo] = useAtom(taiwanShipToAtom)
    const [loading, setLoading] = useAtom(taiwanCheckoutLoadingAtom)
    const [errors, setErrors] = useAtom(taiwanCheckoutErrorsAtom)
    // Distinct from `loading` (which also covers submit): stays false until the
    // initial format (detail-v2) fetch settles, so the page shows a skeleton.
    const [initialized, setInitialized] = useState(false)

    const { login } = useLogin({ onSuccess: () => {} })

    const isAuthenticated = !!(auth.token && auth.baId)
    const useComplexPassword = Boolean((settings as { passwordSettings?: { isUseComplexPassword?: boolean } } | null)?.passwordSettings?.isUseComplexPassword)

    const language = useCurrentLanguage()

    const [accountForm, setAccountForm] = useState(initialTaiwanAccountForm)
    const [shippingForm, setShippingForm] = useState(initialTaiwanShippingForm)

    // TW uses the v1 cart shape: {"onetime":{"flavor_3":1}} (see post-format-api example).
    const shipmentPacks = useCallback(() => JSON.stringify(cart), [cart])

    // Login identity a logged-in member sends with the type=shop /address call.
    const buildLoginFields = useCallback(() => buildTaiwanLoginFields({ auth, customer, userStatus }), [auth, customer, userStatus])

    // Clear any stale errors every time the shipping/checkout page is entered.
    useEffect(() => {
        setErrors([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Prefill the member shipping form once from the logged-in profile.
    // shippingAddress uses firstname + lastname (not a single fullname): prefer
    // the profile's first/last, falling back to splitting the full name.
    const prefilledRef = useRef(false)
    useEffect(() => {
        if (!isAuthenticated || prefilledRef.current) return
        const hn = customer?.humanName
        const addr = customer?.mainAddress
        if (!hn?.fullName && !customer?.email && !addr?.address1) return
        const fullName = hn?.fullName ?? ''
        const [splitFirst, ...splitRest] = fullName.trim().split(/\s+/)
        setShippingForm(prev => ({
            firstname: prev.firstname || hn?.firstName || splitFirst || '',
            lastname: prev.lastname || hn?.lastName || splitRest.join(' ') || '',
            email: prev.email || customer?.email || '',
            phone: prev.phone || customer?.mobilePhone || '',
            address1: prev.address1 || addr?.address1 || '',
            city: prev.city || addr?.city || '',
            zip_code: prev.zip_code || addr?.zip || ''
        }))
        prefilledRef.current = true
    }, [isAuthenticated, customer])

    // On return to the form (back-nav via the stepper), prefill from the saved
    // ship-to captured at submit time — this is what the user last entered, so
    // it takes precedence over the profile prefill above. Skipped when there's
    // no saved data (taiwanShipToAtom is null), leaving profile/empty as-is.
    const shipToPrefilledRef = useRef(false)
    useEffect(() => {
        if (shipToPrefilledRef.current || !shipTo) return
        const [first, ...rest] = (shipTo.name || '').trim().split(/\s+/)
        const patch = {
            firstname: first || '',
            lastname: rest.join(' '),
            email: shipTo.email || '',
            phone: shipTo.phone || '',
            address1: shipTo.address1 || '',
            city: shipTo.city || '',
            zip_code: shipTo.zip || ''
        }
        if (isAuthenticated) setShippingForm(prev => ({ ...prev, ...patch }))
        else setAccountForm(prev => ({ ...prev, ...patch }))
        shipToPrefilledRef.current = true
    }, [shipTo, isAuthenticated])

    // Guards init() against React StrictMode's double-effect (dev) — without it
    // createUuid (POST) + getFormat (GET) fire twice on mount.
    const initStartedRef = useRef(false)

    /** Ensure a checkout UUID + load its format. Reuses ?uuid= if present. */
    const init = useCallback(async () => {
        if (initStartedRef.current) return
        initStartedRef.current = true
        const urlUuid = searchParams.get('uuid')
        setLoading(true)
        setErrors([])
        try {
            let activeUuid = urlUuid || uuid
            if (!activeUuid) {
                const period = await getTaiwanPeriod()
                // Member: type=shop keyed by login (address comes from profile).
                // Guest: type=enroll, personal details captured later via createAccount.
                const res = isAuthenticated
                    ? await postTaiwanShipping({ type: 'shop', shipment_packs: shipmentPacks(), period, ...buildLoginFields() })
                    : await createTaiwanUuid({ type: 'enroll', period, shipment_packs: shipmentPacks() })
                if (!res.success || !res.uuid) {
                    setErrors(formatErrors(res, 'failed_to_load_payment_data', language))
                    return
                }
                activeUuid = res.uuid
                // Surface the freshly-created UUID on the URL so a refresh reuses it.
                setSearchParams({ uuid: activeUuid }, { replace: true })
            }
            setUuid(activeUuid)
            const format = await getTaiwanFormat(activeUuid)
            setFormat({ ...format, uuid: activeUuid })
        } catch {
            setErrors(['failed_to_load_payment_data'])
        } finally {
            setLoading(false)
            setInitialized(true)
        }
        // searchParams/uuid are read-once per init call; deps kept minimal on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const goToPayment = (activeUuid: string) => navigate(`/checkout/payment?uuid=${activeUuid}`)

    /** Guest enrollment: create account → auto-login → payment. */
    const submitGuest = useCallback(async () => {
        setLoading(true)
        setErrors([])
        try {
            const period = await getTaiwanPeriod()
            let activeUuid = uuid || searchParams.get('uuid') || ''
            if (!activeUuid) {
                const res = await createTaiwanUuid({ type: 'enroll', period, shipment_packs: shipmentPacks() })
                if (!res.success || !res.uuid) {
                    setErrors(formatErrors(res, 'create_account_failed', language))
                    return
                }
                activeUuid = res.uuid
                setUuid(activeUuid)
                setSearchParams({ uuid: activeUuid }, { replace: true })
            }

            const payload: TaiwanCreateAccountPayload = {
                type: 'enroll',
                uuid: activeUuid,
                firstname_en: accountForm.firstname,
                lastname_en: accountForm.lastname,
                email: accountForm.email,
                phone: accountForm.phone,
                password: accountForm.password,
                address1: accountForm.address1,
                city: accountForm.city,
                zip: normalizeZip(accountForm.zip_code),
                period,
                shipment_packs: shipmentPacks(),
                ...(accountForm.referralId ? { referral_id: accountForm.referralId } : {})
            }

            const res = await createTaiwanAccount(payload, { skipPasswordCheck: useComplexPassword })
            if (!res.success) {
                // 200 + success:false → surface the Message_v2 validation keys.
                setErrors(formatErrors(res, 'create_account_failed', language))
                return
            }
            if (!res.ba_id) {
                setErrors(['create_account_failed'])
                return
            }

            // Auto-login the freshly-created member (behind the scenes).
            const loggedIn = await login(res.ba_id, accountForm.password)
            if (!loggedIn?.token || !loggedIn?.baId) {
                setErrors(['create_account_failed'])
                return
            }

            const fullName = `${accountForm.firstname} ${accountForm.lastname}`.trim()
            setShipTo({
                name: fullName,
                address1: accountForm.address1,
                city: accountForm.city,
                zip: normalizeZip(accountForm.zip_code),
                email: accountForm.email,
                phone: accountForm.phone
            })

            // Convert the enroll order to a member/shop order on the same uuid
            // (PUT /address type=shop with the new token) so the payment page
            // loads the member format. Mirrors legacy ContinueToPayment / Oman's
            // putOmanMemberShipping. Best-effort — proceed to payment regardless.
            try {
                await postTaiwanShipping({
                    type: 'shop',
                    uuid: activeUuid,
                    shipment_packs: shipmentPacks(),
                    ba_status: readStoredUserStatus() ?? '',
                    login_id: loggedIn.baId,
                    login_name: fullName,
                    login_name_native: fullName,
                    token: loggedIn.token,
                    fullname: '',
                    firstname_en: accountForm.firstname,
                    lastname_en: accountForm.lastname,
                    email: accountForm.email,
                    phone: accountForm.phone,
                    address1: accountForm.address1,
                    city: accountForm.city,
                    zip: normalizeZip(accountForm.zip_code)
                })
            } catch {
                // Non-fatal — payment page re-fetches the format by uuid.
            }
            goToPayment(activeUuid)
        } catch {
            setErrors(['create_account_failed'])
        } finally {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountForm, uuid, useComplexPassword, shipmentPacks])

    /** Member: POST the shop session (login + edited shipping) → payment. */
    const submitMember = useCallback(async () => {
        setLoading(true)
        setErrors([])
        try {
            if (!auth.token) {
                setErrors(['session_expired'])
                return
            }
            const activeUuid = uuid || searchParams.get('uuid') || ''
            const payload: TaiwanShippingPayload = {
                type: 'shop',
                // uuid present → postTaiwanShipping switches to PUT (see fg-put.txt).
                ...(activeUuid ? { uuid: activeUuid } : {}),
                shipment_packs: shipmentPacks(),
                ...buildLoginFields(),
                // Name is sent split (firstname_en/lastname_en); fullname stays empty.
                fullname: '',
                firstname_en: shippingForm.firstname,
                lastname_en: shippingForm.lastname,
                email: shippingForm.email,
                phone: shippingForm.phone,
                address1: shippingForm.address1,
                city: shippingForm.city,
                zip: normalizeZip(shippingForm.zip_code)
            }
            const res = await postTaiwanShipping(payload)
            // On success proceed — the PUT (update) may not echo a uuid, so fall
            // back to the one we already hold.
            if (!res.success) {
                setErrors(formatErrors(res, 'failed_to_update_address', language))
                return
            }
            const nextUuid = res.uuid || activeUuid
            if (!nextUuid) {
                setErrors(['failed_to_update_address'])
                return
            }
            setShipTo({
                name: `${shippingForm.firstname} ${shippingForm.lastname}`.trim(),
                address1: shippingForm.address1,
                city: shippingForm.city,
                zip: normalizeZip(shippingForm.zip_code),
                email: shippingForm.email,
                phone: shippingForm.phone
            })
            setUuid(nextUuid)
            setSearchParams({ uuid: nextUuid }, { replace: true })
            goToPayment(nextUuid)
        } catch {
            setErrors(['failed_to_update_address'])
        } finally {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shippingForm, auth, buildLoginFields, shipmentPacks, language])

    // ── Login-on-checkout → fresh member session ─────────────────────────────
    // A guest can log in via LoginBar on this page. On success start a NEW member
    // (type=shop) session — a brand-new uuid — and move the URL to
    // /checkout/shipping so link + order summary reflect the logged-in member.
    // Gated on the profile being loaded (useLogin sets customer after auth), so
    // the shop session carries the correct login identity.
    const [pendingLoginSwitch, setPendingLoginSwitch] = useState(false)
    const loginSwitchRef = useRef(false)
    const handleLoginSuccess = useCallback(() => setPendingLoginSwitch(true), [])

    useEffect(() => {
        if (!pendingLoginSwitch || !isAuthenticated || loginSwitchRef.current) return
        const profileReady = !!(customer?.email || customer?.humanName?.fullName || customer?.mainAddress?.address1)
        if (!profileReady) return
        loginSwitchRef.current = true
        void (async () => {
            setLoading(true)
            setErrors([])
            try {
                const period = await getTaiwanPeriod()
                const res = await postTaiwanShipping({ type: 'shop', shipment_packs: shipmentPacks(), period, ...buildLoginFields() })
                if (!res.success || !res.uuid) {
                    setErrors(formatErrors(res, 'failed_to_load_payment_data', language))
                    return
                }
                setUuid(res.uuid)
                const format = await getTaiwanFormat(res.uuid)
                setFormat({ ...format, uuid: res.uuid })
                navigate(`/checkout/shipping?uuid=${res.uuid}`, { replace: true })
            } catch {
                setErrors(['failed_to_load_payment_data'])
            } finally {
                setLoading(false)
                setPendingLoginSwitch(false)
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingLoginSwitch, isAuthenticated, customer])

    return {
        isAuthenticated,
        accountForm,
        setAccountForm,
        shippingForm,
        setShippingForm,
        submitGuest,
        submitMember,
        handleLoginSuccess,
        init,
        initialized,
        loading,
        errors
    }
}
